import FormField from 'components/FormField';
import { AiModelState, getPrefixName, Inline, Prefix, stores2storeScopes } from './share';
import { Button, Radio } from 'antd';
import { useCallback, useMemo } from 'react';
import { updatePrivateShipping } from 'views/BootGuide/service';
import { getStoreCount } from 'components/StoreScopes';
import useSmartField from 'hooks/useSmartField';
import ItemSelect from '@knowledge/knowledges/general/edit/selectors/ItemSelect';
import { FormOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import ModalMode from './ModalMode';
import { useTranslate } from '@tolgee/react';

type FV = BootGuide.AiModel.FormValues.PrivateShipping;

export function useLabel() {
  const { t } = useTranslate();

  return useMemo(() => {
    return {
      noSupport: t('不支持'),
      support: t('支持'),
    } as const;
  }, [t]);
}

function PrivateShipping({ prefix }: { prefix?: Prefix }) {
  const label = useLabel();

  return (
    <FormField<FV['value']> name={getPrefixName('value', prefix)}>
      {({ field: { value, onChange } }) => {
        return (
          <div className="mt-[6px]">
            {Object.keys(label).map((key) => {
              return (
                <div key={key} className="mb-2">
                  <Radio
                    checked={value === key}
                    onChange={({ target: { checked } }) => checked && onChange(key)}
                  >
                    {label[key]}
                  </Radio>
                </div>
              );
            })}
            <ItemsContainer background="var(--fett-erp-card-bg)">
              <Items prefix={prefix} />
            </ItemsContainer>
          </div>
        );
      }}
    </FormField>
  );
}

function Items({ prefix }: { prefix?: Prefix }) {
  const { t } = useTranslate();

  const {
    field: { value },
  } = useSmartField<FV['value']>(getPrefixName('value', prefix));
  const {
    field: { value: storeScopes },
  } = useSmartField<FV['storeScopes']>(getPrefixName('storeScopes', prefix));
  const {
    field: { value: items = [], onChange: onItemsChange },
  } = useSmartField<FV['items']>(getPrefixName('items', prefix));

  if (value !== 'support' || !getStoreCount(storeScopes)) {
    return <></>;
  }

  return (
    <ItemSelect
      storeScopes={storeScopes}
      storeType="ALL"
      value={items}
      onChange={onItemsChange}
      emptyRender={(show) => (
        <Inline>
          {t('全部商品')}
          <Button type="text" icon={<FormOutlined />} onClick={show} />
        </Inline>
      )}
    />
  );
}

export function PrivateShippingBootGuide(props: AiModelState<FV>) {
  const label = useLabel();

  return (
    <ModalMode
      state={props}
      title={props.t('私密发货')}
      tips={props.t(
        '最后，还有关于「其他」的2个小问题，您的店铺是否支持私密发货呢?以下是一些常见的选项供您选择和调整',
      )}
      form={<PrivateShipping />}
      getFirstInitialValues={useCallback(({ stores }): { values: FV[] } => {
        return {
          values: [
            { value: 'noSupport', storeScopes: stores2storeScopes(stores), items: [] },
            { value: 'support', storeScopes: [], items: [] },
          ],
        };
      }, [])}
      updateApi={updatePrivateShipping}
      getFirstFormItem={({ value }, index, storeSelect) => {
        return (
          <div>
            <section>
              {label[value]}
              {storeSelect}
            </section>
            <ItemsContainer background="#fff">
              <Items prefix={['values', index]} />
            </ItemsContainer>
          </div>
        );
      }}
      getFirstFormStoreSelectKey={({ value }) => value}
    />
  );
}

const ItemsContainer = styled.div<{ background: string }>`
  > div {
    padding: 4px 12px;
    background: ${({ background }) => background};
  }
`;

export default PrivateShipping;
